<script setup lang="ts">
// NPM
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import {
  BaseAvatar,
  BaseBadge,
  getDisplayName,
  getInitials
} from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';
import type { UserDashboardRelationsGetHelperRelationsResponseType } from '@magma-app/magma-types/all';

// Stores
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import {
  RELATION_STATE_COLORS,
  RELATION_STATE_ICONS
} from '@/composables/relationPresentation';

const relationsStore = useRelationsStore();
const userStore = useUserStore();
const { t, locale } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  active: {
    type: Boolean,
    default: false
  },
  relation: {
    type: Object as PropType<
      UserDashboardRelationsGetHelperRelationsResponseType['toProcess'][number]
    >,
    required: true
  }
});

// Event Emitters
const emit = defineEmits(['click']);

// Computed
const hasMultipleCampaigns = computed(
  () => relationsStore.relations.hasMultipleCampaigns
);

const relatedCampaign = computed(() =>
  userStore.campaigns.find(
    (campaign) => campaign.id === props.relation.campaignId
  )
);

const displayName = computed(() =>
  getDisplayName(
    props.relation.firstname,
    props.relation.lastname,
    props.relation.email
  )
);

// Age adds attention to an unstarted relation without replacing its actual state.
const needsContactReminder = computed(
  () => props.relation.state === 'toContact' && props.relation.isInactive
);
const badgeColor = computed(() =>
  needsContactReminder.value
    ? 'yellow'
    : RELATION_STATE_COLORS[props.relation.state] || 'grey'
);
const contactAgeDays = computed(() => {
  if (!props.relation.createdAt) return null;
  const createdAt = new Date(props.relation.createdAt).getTime();
  return Number.isFinite(createdAt)
    ? Math.max(0, Math.floor((Date.now() - createdAt) / 86400000))
    : null;
});
const badgeLabel = computed(() => {
  const label = t(`helperRelation.state.${props.relation.state}.badge`);
  if (!needsContactReminder.value || contactAgeDays.value === null)
    return label;
  return t('relationsLayout.row.contactAge', {
    label,
    count: contactAgeDays.value > 99 ? '99+' : contactAgeDays.value
  });
});
const badgeIcon = computed(() =>
  needsContactReminder.value
    ? 'clock'
    : RELATION_STATE_ICONS[props.relation.state]
);
const statusDescription = computed(() =>
  needsContactReminder.value
    ? contactAgeDays.value === null
      ? t('relationsLayout.row.contactReminder')
      : t('relationsLayout.row.contactReminderDays', {
          count: contactAgeDays.value
        })
    : props.relation.state === 'waitingHelpee'
      ? t('helperRelation.state.waitingHelpee.hint', {
          name: props.relation.firstname || displayName.value
        })
      : badgeLabel.value
);

const relativeAge = computed(() => {
  const updatedAt = new Date(props.relation.updatedAt).getTime();
  if (Number.isNaN(updatedAt)) return '';

  const days = Math.floor((Date.now() - updatedAt) / (24 * 60 * 60 * 1000));

  if (days < 1) return t('relationsLayout.row.today');
  if (days < 30) return t('relationsLayout.row.daysAgo', { count: days });

  return new Date(props.relation.updatedAt).toLocaleDateString(locale.value, {
    day: 'numeric',
    month: 'short'
  });
});

// Event Listeners
const onClick = () => {
  emit('click', props.relation);
};
</script>

<template>
  <div
    role="button"
    tabindex="0"
    :aria-current="active ? 'true' : undefined"
    :aria-label="`${displayName}, ${statusDescription}`"
    class="relation-list-row relative flex w-full cursor-pointer items-center gap-3 border-t border-border-subtle px-4 py-4 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-brand-emphasis"
    :class="
      active ? 'bg-background-brand-default' : 'hover:bg-background-subtle'
    "
    @click="onClick"
    @keydown.enter.prevent="onClick"
    @keydown.space.prevent="onClick"
  >
    <BaseAvatar
      :fallback="
        getInitials(relation.firstname, relation.lastname, relation.email)
      "
      :src="relation.profilePictureUrl"
      size="md"
      class="shrink-0"
    />

    <div
      class="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2.5"
    >
      <div class="flex min-w-0 items-center gap-1.5">
        <span
          v-if="!relation.isRead"
          class="h-1.5 w-1.5 shrink-0 rounded-full bg-background-brand-emphasis"
          :aria-label="$t('relationsLayout.row.unread')"
        ></span>
        <span
          class="truncate text-mgm-txt-sm"
          :title="displayName"
          :class="[
            relation.isRead ? 'font-medium' : 'font-semibold',
            active
              ? 'text-foreground-brand-default'
              : 'text-foreground-emphasis'
          ]"
        >
          {{ displayName }}
        </span>
      </div>
      <BaseBadge
        size="xs"
        class="relation-status max-w-[170px] justify-self-end whitespace-nowrap"
        :class="{ 'relation-status-reminder': needsContactReminder }"
        :color="badgeColor"
        :text="badgeLabel"
        :icon-type="badgeIcon ? 'icon' : 'none'"
        :icon-name="badgeIcon"
        :title="statusDescription"
      />
      <div class="col-span-2 flex min-w-0 items-center justify-between gap-3">
        <span
          class="min-w-0 truncate text-mgm-txt-xs text-foreground-subtle"
          :title="relatedCampaign?.title"
        >
          {{ hasMultipleCampaigns ? relatedCampaign?.title : '' }}
        </span>
        <span
          class="shrink-0 text-right text-mgm-txt-xs tabular-nums text-foreground-subtle"
        >
          {{ relativeAge }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.relation-list-row {
  container-type: inline-size;
}

/* Respond to the list pane's width, including narrow desktop panes. */
@container (max-width: 320px) {
  .relation-status {
    max-width: 128px;
    height: auto;
    min-height: 16px;
    text-align: center;
  }

  .relation-status :deep(span) {
    white-space: normal;
    overflow: visible;
  }

  .relation-status-reminder {
    max-width: none;
  }

  .relation-status-reminder :deep(span) {
    white-space: nowrap;
  }
}
</style>
